import { useMemo } from "react";
import { useServices } from "@core/servicesContext";
import { StorePresentation } from "@core/store/storePresentation";
import {
	AsyncResult,
	PaginatedDataResult,
	PromiseResult,
	useMappedStore,
	usePaginatedStore,
	usePromise,
	useStoredData,
} from "@utils/hooks";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { CourseStatus } from "@domain/lms/course/courseDraft";
import { Quiz } from "./quiz";

export function useQuiz(id: string): AsyncResult<Quiz> {
	const { quizService } = useServices();
	const currentProfile = useCurrentProfile();
	return useStoredData(id, quizService.quizStore, [currentProfile?.id]);
}

export function useMyQuizzes(status: CourseStatus | "ALL"): PaginatedDataResult<Quiz> {
	const { quizService } = useServices();
	return useMappedStore(status, quizService.myQuizzesStore, [status]);
}

export function useSuggestedQuizzes(count: number): PaginatedDataResult<Quiz> {
	const { quizService } = useServices();

	const store = useMemo(() => new StorePresentation(quizService.quizStore, () => quizService.suggest(count)), [count]);
	return usePaginatedStore(store, [count]);
}

export function usePersonQuizzes(personId: string, organization?: boolean): PaginatedDataResult<Quiz> {
	const { quizService } = useServices();
	return useMappedStore(
		personId,
		organization ? quizService.organizationQuizzesStore : quizService.profileQuizzesStore,
		[personId, organization]
	);
}

export function useWorkspaceQuizzes(workspaceId: string): PaginatedDataResult<Quiz> {
	const { quizService } = useServices();
	return useMappedStore(workspaceId, quizService.workspaceQuizzesStore, [workspaceId]);
}

export function useQuizParticipantAnswer(quizId: string, participantId: string): PromiseResult<Quiz> {
	const { quizService } = useServices();
	return usePromise(() => quizService.fetchParticpantAnswer(quizId, participantId), [quizId, participantId]);
}
